import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Download, ThumbUp, ThumbDown, ChatBubbleOutline } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/config';

const BASE = `${API_BASE_URL}/public/quote`;

interface PublicQuote {
  QuoteNumber: string;
  Title: string | null;
  ClientName: string | null;
  GrandTotal: number;
  Currency: string;
  ValidUntil: string | null;
  lineItems: { Description: string | null; Quantity: number; Unit: string | null; UnitPrice: number; LineTotal: number }[];
}

type ActionType = 'accept' | 'decline' | 'request-changes' | null;

export const PublicAcceptPage = () => {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionDone, setActionDone] = useState(false);
  const [actionDialog, setActionDialog] = useState<ActionType>(null);
  const [submitting, setSubmitting] = useState(false);

  // Accept form
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [poNumber, setPoNumber] = useState('');
  // Decline form
  const [declineReason, setDeclineReason] = useState('');
  // Request changes form
  const [changeComment, setChangeComment] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/${token}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Invalid or expired link.');
        return res.json();
      })
      .then((data) => setQuote(data))
      .catch((err) => setError(err.message || 'Failed to load quote.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submitAction = useCallback(async (action: ActionType) => {
    if (!token || !action) return;
    setSubmitting(true);
    try {
      let body: Record<string, string> = {};
      if (action === 'accept') body = { fullName, email, poNumber };
      if (action === 'decline') body = { reason: declineReason };
      if (action === 'request-changes') body = { comment: changeComment };

      const res = await fetch(`${BASE}/${token}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Action failed.');
      setActionDone(true);
      setActionDialog(null);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token, fullName, email, poNumber, declineReason, changeComment]);

  const headerSx = {
    bgcolor: '#3E4A3E', color: '#fff', py: 3, px: 4, borderRadius: '12px 12px 0 0',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#3E4A3E' }} />
      </Box>
    );
  }

  if (error && !quote) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>{error}</Alert>
      </Box>
    );
  }

  if (!quote) return null;

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: quote.Currency || 'CAD' }).format(v);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, px: 2, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Card sx={{ maxWidth: 800, width: '100%', borderRadius: '12px', overflow: 'visible' }} elevation={3}>
        {/* Header */}
        <Box sx={headerSx}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>BGC Engineering</Typography>
          <Typography variant="subtitle2" sx={{ opacity: 0.85, mt: 0.5 }}>Geotechnical Laboratory</Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          {actionDone ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Thank you! Your response has been recorded. You may close this page.
            </Alert>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quote {quote.QuoteNumber}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3 }}>
                <InfoRow label="Title" value={quote.Title || '-'} />
                <InfoRow label="Client" value={quote.ClientName || '-'} />
                <InfoRow label="Total" value={formatCurrency(quote.GrandTotal)} />
                <InfoRow label="Valid Until" value={quote.ValidUntil ? new Date(quote.ValidUntil).toLocaleDateString() : '-'} />
              </Box>

              {/* Line items */}
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quote.lineItems.map((li, i) => (
                      <TableRow key={i}>
                        <TableCell>{li.Description || '-'}</TableCell>
                        <TableCell align="right">{li.Quantity}</TableCell>
                        <TableCell>{li.Unit || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(li.UnitPrice)}</TableCell>
                        <TableCell align="right">{formatCurrency(li.LineTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'right', mb: 3 }}>
                Grand Total: {formatCurrency(quote.GrandTotal)}
              </Typography>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" color="success" startIcon={<ThumbUp />}
                  onClick={() => setActionDialog('accept')} sx={{ textTransform: 'none' }}>
                  Accept Quote
                </Button>
                <Button variant="outlined" color="error" startIcon={<ThumbDown />}
                  onClick={() => setActionDialog('decline')} sx={{ textTransform: 'none' }}>
                  Decline
                </Button>
                <Button variant="outlined" startIcon={<ChatBubbleOutline />}
                  onClick={() => setActionDialog('request-changes')} sx={{ textTransform: 'none' }}>
                  Request Changes
                </Button>
                <Button variant="outlined" startIcon={<Download />}
                  onClick={() => window.open(`${BASE}/${token}/pdf`, '_blank')}
                  sx={{ textTransform: 'none', ml: 'auto' }}>
                  Download PDF
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Card>

      {/* Accept Dialog */}
      <Dialog open={actionDialog === 'accept'} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Accept Quote</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)}
            required fullWidth autoFocus />
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required fullWidth />
          <TextField label="PO Number (optional)" value={poNumber} onChange={(e) => setPoNumber(e.target.value)}
            fullWidth />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="success" disabled={submitting || !fullName || !email}
            onClick={() => submitAction('accept')} sx={{ textTransform: 'none' }}>
            {submitting ? 'Submitting...' : 'Confirm Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={actionDialog === 'decline'} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Decline Quote</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField label="Reason (optional)" value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)} multiline rows={3} fullWidth autoFocus />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={submitting}
            onClick={() => submitAction('decline')} sx={{ textTransform: 'none' }}>
            {submitting ? 'Submitting...' : 'Confirm Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Changes Dialog */}
      <Dialog open={actionDialog === 'request-changes'} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Request Changes</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField label="Comment" value={changeComment}
            onChange={(e) => setChangeComment(e.target.value)} multiline rows={3} fullWidth required autoFocus />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={submitting || !changeComment}
            onClick={() => submitAction('request-changes')} sx={{ textTransform: 'none' }}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

export default PublicAcceptPage;
